import React, { useEffect, useRef, useState } from "react";
import { Line } from "rc-progress";
import check from "../../assets/icons/Icon.svg";
import PropTypes from 'prop-types';
import { useAuth } from "../../components/AuthContext/AuthContext";


const STEP_LABELS = [
    "Test kit registered",
    "Awaiting sample return",
    "Test kit received at lab",
    "Awaiting Doctor Review",
    "Results ready",
];

const DNA_STEP_LABELS = [
    "Test kit registered",
    "Awaiting sample return",
    "Test kit received at lab",
    "Results ready",
];

const TestKitTracker = ({
    totalSteps,
    currentStepStatus,
    totalCompletedSteps,
    isReoportPublished,
    dateTimeMessage,
    isMobile,
    isNotDna }) => {
    const auth = useAuth();
    const containerRef = useRef(null);
    const [progressLineHeight, setProgressLineHeight] = useState(0);

    const calculateProgressPercentage = () => {
        if(!isMobile && totalCompletedSteps === 0)
        {
            return 3;
        }
        if (isReoportPublished) {
            return 100;
        }

        if (totalSteps <= 1) return 0;
        const maxSteps = isNotDna? 4 : 3 

        const percentage = (totalCompletedSteps / maxSteps) * 100;
        return Math.floor(percentage);
    };

    const progressPercentage = calculateProgressPercentage();
    

    useEffect(() => {
        if (isReoportPublished) {
            auth.setIsCompleted(true);
        }
    }, [isReoportPublished, auth]);

 useEffect(() => {
        if (isMobile && containerRef.current) {
            const calculateHeight = () => {
                const steps = containerRef.current.querySelectorAll('.test-kit-step');
                
                if (steps.length === 0) return;

                const firstStep = steps[0];
                const lastStep = steps[steps.length - 1];
                
                if (!firstStep || !lastStep) return;

                const firstCircle = firstStep.querySelector('.stepper-icon button') || 
                                  firstStep.querySelector('button');
                const lastCircle = lastStep.querySelector('.stepper-icon button') || 
                                 lastStep.querySelector('button');
                
                if (!firstCircle || !lastCircle) return;

                const firstCircleRect = firstCircle.getBoundingClientRect();
                const lastCircleRect = lastCircle.getBoundingClientRect();
                const containerRect = containerRef.current.getBoundingClientRect();

                const firstCircleCenter = firstCircleRect.top + (firstCircleRect.height / 2) - containerRect.top;
                const lastCircleCenter = lastCircleRect.top + (lastCircleRect.height / 2) - containerRect.top;
                
                const calculatedHeight = lastCircleCenter - firstCircleCenter;
                
                setProgressLineHeight(calculatedHeight);
            };

            calculateHeight();

            window.addEventListener('resize', calculateHeight);
            
            const observer = new MutationObserver(calculateHeight);
            observer.observe(containerRef.current, {
                childList: true,
                subtree: true,
                attributes: true
            });

            const timer = setTimeout(calculateHeight, 100);

            return () => {
                window.removeEventListener('resize', calculateHeight);
                observer.disconnect();
                clearTimeout(timer);
            };
        }
    }, [isMobile, totalSteps, totalCompletedSteps]);


    const getStepClass = (index) => {
        if (isReoportPublished) {
            return "step completed";
        }

        if (index < totalCompletedSteps) {
            return "step completed";
        }

        if (index === totalCompletedSteps) {
            return "step current-step";
        }

        return "step";
    };

    const renderStepContent = (index) => {
        if (isReoportPublished) {
            return <img src={check} alt="" className="completed-icon" />;
        }

        if (index <= totalCompletedSteps) {
            return <img src={check} alt="" className="completed-icon" />;
        }

        return null;
    };

    const getStrokeColor = () => {
        if (isReoportPublished) {
            return "#48A49D";
        }

        if (totalCompletedSteps >= 0 && currentStepStatus) {
            return "#48A49D";
        }

        return "#CFD6DC";
    };

    const getLabelClass = (index) => {
        if (isReoportPublished) {
            return "step-label completed-label";
        }

        if (index < totalCompletedSteps) {
            return "step-label completed-label";
        }

        if (index === totalCompletedSteps) {
            return "step-label current-label";
        }

        return "step-label";
    };
    return (
        <div
            className="progress-bar-container test-tracker-bar"
            data-testid="progress-bar"
            name="progress-bar"
            ref={containerRef}
        >
            <Line
                percent={progressPercentage}
                strokeWidth="5"
                trailWidth="5"
                strokeColor={getStrokeColor()}
                trailColor="#C6C6C6"
                style={{ width: isMobile ? `${progressLineHeight}px` : '100%' }}
            />
            <div className="steps container-fluid">
                {Array.from({ length: totalSteps }, (_, index) => {
                    return (
                        <div className="row" key={isMobile ? `step-row-${index}` : undefined} >
                            <div className={`col-md-12 d-flex flex-column align-items-center ${isMobile && 'test-kit-step'}`}>
                                <div key={`step-${index}`} className={getStepClass(index)}>
                                    {isMobile ?
                                        <div className="stepper-icon">
                                            <button className={getStepClass(index)}>
                                                {renderStepContent(index)}
                                            </button>
                                        </div>
                                        :
                                        <button className={getStepClass(index)}>
                                            {renderStepContent(index)}
                                        </button>
                                    }
                                </div>
                                {isMobile ?
                                    <div className="stepper-info">
                                        <span className={getLabelClass(index)}>
                                            {isNotDna ? STEP_LABELS[index] : DNA_STEP_LABELS[index]}
                                        </span>
                                        {index <= totalCompletedSteps && dateTimeMessage(index)}
                                    </div>
                                    :
                                    <>
                                        <span className={getLabelClass(index)}>
                                            {isNotDna ? STEP_LABELS[index] : DNA_STEP_LABELS[index]}
                                        </span>
                                        {index <= totalCompletedSteps && dateTimeMessage(index)}
                                    </>
                                }

                            </div>
                        </div>
                        
                    );
                    
                })}
                
            </div>
        </div>
    );
}

TestKitTracker.propTypes = {
    totalSteps: PropTypes.number.isRequired,
    currentStepStatus: PropTypes.bool.isRequired,
    totalCompletedSteps: PropTypes.number.isRequired,
    isReoportPublished: PropTypes.bool.isRequired,
    dateTimeMessage: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired
};

export default TestKitTracker;